/**
 * Checklist Config Component
 *
 * Admin view for creating and editing checklists.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OpsSubpageHeadingClassic } from "@/components/ops/OpsSubpageHeadingClassic";
import {
  faArrowLeft,
  faCircleNotch,
  faPlus,
  faEdit,
  faTrash,
  faGripVertical,
  faSave,
  faToggleOn,
  faToggleOff,
  faImage,
  faLink,
  faListUl,
  faBold,
  faItalic,
  faFileExcel,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Badge } from "@/components/ui/badge/Badge";
import { useNotification } from "@/context/NotificationContext";
import {
  getChecklists,
  getChecklist,
  createChecklist,
  updateChecklist,
  deleteChecklist,
  uploadScreenshot,
  type Checklist,
  type ChecklistStep,
  type Weekday,
  type TimmyReviewDefault,
  OPS_TEAM_MEMBERS,
} from "@/services/api/opsChecklists";
import { useDebounce } from "@/hooks/useDebounce";
import { ChecklistInstructionsMarkdown } from "./ChecklistInstructionsMarkdown";
import { ChecklistBulletPoints } from "./ChecklistBulletPoints";
import { downloadChecklistsXlsx } from "./exportChecklistsXlsx";

/** Explicit shape for PATCH/POST checklist steps (definition fields only). */
function stepsForChecklistApi(steps: ChecklistStep[]): ChecklistStep[] {
  return steps.map((s) => ({
    step_id: s.step_id,
    order: s.order,
    title: s.title,
    instructions: s.instructions,
  }));
}

function normalizeTimmyReviewDefault(
  v: Checklist["timmy_review_default"]
): TimmyReviewDefault {
  return v === "skip" ? "skip" : "required";
}

function normalizedBulletPoints(points: string[]): string[] {
  return points.map((s) => s.trim()).filter(Boolean);
}

function formatChecklistSaveError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { detail?: unknown } | undefined;
    const detail = data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      const parts = detail.map((item: unknown) =>
        typeof item === "object" && item !== null && "msg" in item
          ? String((item as { msg?: unknown }).msg ?? item)
          : JSON.stringify(item)
      );
      return parts.join("; ") || "Validation failed";
    }
    if (detail != null && typeof detail !== "object") return String(detail);
    return error.message || "Failed to save checklist";
  }
  return error instanceof Error ? error.message : "Failed to save checklist";
}

const WEEKDAYS: { value: Weekday; label: string }[] = [
  { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" },
  { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" },
  { value: "friday", label: "Fri" },
  { value: "saturday", label: "Sat" },
  { value: "sunday", label: "Sun" },
];

/** Daily deadline stored as 24-hour HH:MM; empty means none. */
const DEADLINE_HHMM_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;

function isValidDeadlineHhMm(value: string): boolean {
  if (!value.trim()) return true;
  return DEADLINE_HHMM_RE.test(value.trim());
}

/** Pads valid H:M to HH:MM; leaves empty or non-matching input unchanged. */
function normalizeDeadlineHhMm(raw: string): string {
  const t = raw.trim();
  if (!t) return "";
  const m = t.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!m) return t;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h < 0 || h > 23 || min < 0 || min > 59) return t;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

/** Suggested Markdown link label when the author leaves "Link text" empty. */
function defaultInstructionLinkLabel(href: string): string {
  const t = href.trim();
  if (!t) return "Link";
  if (t.startsWith("/")) {
    const parts = t.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    if (last && /^[\w.-]+$/i.test(last)) {
      return last
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
    }
    return "Open in app";
  }
  if (t.startsWith("mailto:")) {
    const addr = t.slice("mailto:".length).split("?")[0];
    return addr?.trim() || "Email";
  }
  if (t.startsWith("tel:")) {
    return t.slice("tel:".length).trim() || "Phone";
  }
  try {
    const u = new URL(t);
    return u.hostname.replace(/^www\./i, "");
  } catch {
    return "Link";
  }
}

const INSTRUCTION_LINK_SHORTCUTS: { label: string; href: string }[] = [
  { label: "Ops checklists", href: "/ops/checklists" },
  { label: "Ops hub", href: "/ops" },
  { label: "Guardians", href: "/ops/guardians" },
  { label: "Progeny", href: "/ops/progeny" },
  { label: "Screener", href: "/screener" },
];

interface InstructionLinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (markdown: string) => void;
}

function InstructionLinkDialog({
  isOpen,
  onClose,
  onInsert,
}: InstructionLinkDialogProps) {
  const [href, setHref] = useState("");
  const [label, setLabel] = useState("");

  useEffect(() => {
    if (isOpen) {
      setHref("");
      setLabel("");
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const url = href.trim();
    if (!url) return;
    const rawDisplay = label.trim() || defaultInstructionLinkLabel(url);
    const safeLabel = rawDisplay.replace(/[[\]]/g, "").trim() || "Link";
    onInsert(`[${safeLabel}](${url})`);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const previewLabel = label.trim() || defaultInstructionLinkLabel(href.trim());

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full border border-slate-200 dark:border-slate-700 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="instruction-link-dialog-title"
      >
        <div className="px-5 pt-5 pb-3 border-b border-slate-200 dark:border-slate-700">
          <h3
            id="instruction-link-dialog-title"
            className="text-lg font-semibold text-slate-900 dark:text-white"
          >
            Add instruction link
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            HTTPS, mailto, tel, or an in-app path such as{" "}
            <code className="text-xs bg-slate-100 dark:bg-slate-900/80 px-1 py-0.5 rounded">
              /ops/checklists
            </code>
            .
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <div>
            <span className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
              Quick fill
            </span>
            <div className="flex flex-wrap gap-2">
              {INSTRUCTION_LINK_SHORTCUTS.map((s) => (
                <button
                  key={s.href}
                  type="button"
                  onClick={() => setHref(s.href)}
                  className="px-2.5 py-1 text-xs font-medium rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/80 transition-colors"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="instruction-link-href"
              className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5"
            >
              URL or path
            </label>
            <input
              id="instruction-link-href"
              type="text"
              value={href}
              onChange={(e) => setHref(e.target.value)}
              placeholder="https://… or /ops/…"
              autoComplete="off"
              autoFocus
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono"
            />
          </div>

          <div>
            <label
              htmlFor="instruction-link-label"
              className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5"
            >
              Link text{" "}
              <span className="font-normal text-slate-500 dark:text-slate-400">
                (optional)
              </span>
            </label>
            <input
              id="instruction-link-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Shown in instructions; leave blank to auto-pick"
              autoComplete="off"
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            {href.trim() ? (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
                Preview:{" "}
                <span className="text-indigo-600 dark:text-sky-300 underline underline-offset-2">
                  {previewLabel}
                </span>
              </p>
            ) : null}
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!href.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Insert link
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Renders instruction preview from debounced text so ReactMarkdown does not run on every keystroke. */
function StepInstructionPreview({
  source,
}: {
  source: string;
}) {
  const debounced = useDebounce(source, 450);
  if (!source.trim()) return null;
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-900/40 p-2 max-h-56 overflow-y-auto">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-500 mb-1.5">
        Preview
      </div>
      {debounced.trim() ? (
        <ChecklistInstructionsMarkdown
          content={debounced}
          compact
          className="!text-slate-700 dark:!text-slate-200"
        />
      ) : (
        <p className="text-xs text-slate-500 dark:text-slate-500 italic">
          Preview will update when you stop typing.
        </p>
      )}
    </div>
  );
}

interface StepEditorProps {
  steps: ChecklistStep[];
  onChange: (steps: ChecklistStep[]) => void;
}

function StepEditor({ steps, onChange }: StepEditorProps) {
  const { addNotification } = useNotification();
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [uploadingForStepId, setUploadingForStepId] = useState<string | null>(
    null
  );
  const [linkDialogStepIndex, setLinkDialogStepIndex] = useState<number | null>(
    null
  );

  const closeLinkDialog = useCallback(() => setLinkDialogStepIndex(null), []);

  const addStep = () => {
    const newStep: ChecklistStep = {
      step_id: `step-${Date.now()}`,
      order: steps.length + 1,
      title: "",
      instructions: "",
    };
    onChange([...steps, newStep]);
  };

  const updateStep = (index: number, updates: Partial<ChecklistStep>) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], ...updates };
    onChange(newSteps);
  };

  const insertAtCursor = (index: number, text: string) => {
    const step = steps[index];
    if (!step) return;
    const el = textareaRefs.current[step.step_id];
    const value = step.instructions;
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    const newValue = value.slice(0, start) + text + value.slice(end);
    updateStep(index, { instructions: newValue });
    requestAnimationFrame(() => {
      const ta = textareaRefs.current[step.step_id];
      if (ta) {
        ta.focus();
        const pos = start + text.length;
        ta.setSelectionRange(pos, pos);
      }
    });
  };

  const insertBulletMarkdownLine = (index: number) => {
    const step = steps[index];
    if (!step) return;
    const value = step.instructions;
    const el = textareaRefs.current[step.step_id];
    const start = el ? el.selectionStart : value.length;
    const atLineStart = start === 0 || value[start - 1] === "\n";
    insertAtCursor(index, atLineStart ? "- " : "\n- ");
  };

  /** Wrap selection with Markdown bold / italic. Empty selection inserts delimiter pairs only (no placeholder words). */
  const wrapInstructionMarkdown = (index: number, kind: "bold" | "italic") => {
    const step = steps[index];
    if (!step) return;
    const el = textareaRefs.current[step.step_id];
    const value = step.instructions;
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    const selected = value.slice(start, end);

    if (selected.length > 0) {
      const wrap = kind === "bold" ? "**" : "*";
      const insertion = `${wrap}${selected}${wrap}`;
      const newValue = value.slice(0, start) + insertion + value.slice(end);
      updateStep(index, { instructions: newValue });
      requestAnimationFrame(() => {
        const ta = textareaRefs.current[step.step_id];
        if (ta) {
          ta.focus();
          const innerStart = start + wrap.length;
          ta.setSelectionRange(innerStart, innerStart + selected.length);
        }
      });
      return;
    }

    if (kind === "bold") {
      const insertion = "****";
      const newValue = value.slice(0, start) + insertion + value.slice(end);
      updateStep(index, { instructions: newValue });
      requestAnimationFrame(() => {
        const ta = textareaRefs.current[step.step_id];
        if (ta) {
          ta.focus();
          ta.setSelectionRange(start + 2, start + 2);
        }
      });
      return;
    }

    const zwsp = "\u200B";
    const insertion = `*${zwsp}*`;
    const newValue = value.slice(0, start) + insertion + value.slice(end);
    updateStep(index, { instructions: newValue });
    requestAnimationFrame(() => {
      const ta = textareaRefs.current[step.step_id];
      if (ta) {
        ta.focus();
        ta.setSelectionRange(start + 1, start + 2);
      }
    });
  };

  const handleInstructionLinkInsert = (markdown: string) => {
    if (linkDialogStepIndex === null) return;
    insertAtCursor(linkDialogStepIndex, markdown);
  };

  const handleImageFile = async (index: number, file: File | undefined) => {
    if (!file) return;
    const step = steps[index];
    if (!step) return;
    setUploadingForStepId(step.step_id);
    try {
      const { url } = await uploadScreenshot(file);
      const insert = `\n![image](${url})\n`;
      insertAtCursor(index, insert);
      addNotification({
        type: "success",
        title: "Image added",
        message: "Added to step instructions as Markdown.",
      });
    } catch (e) {
      console.error(e);
      addNotification({
        type: "error",
        title: "Upload failed",
        message: "Image upload failed. Try again or paste a URL as ![alt](https://...).",
      });
    } finally {
      setUploadingForStepId(null);
      const input = fileInputRefs.current[step.step_id];
      if (input) input.value = "";
    }
  };

  const removeStep = (index: number) => {
    const newSteps = steps.filter((_, i) => i !== index);
    // Reorder
    newSteps.forEach((s, i) => {
      s.order = i + 1;
    });
    onChange(newSteps);
  };

  const moveStep = (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === steps.length - 1)
    ) {
      return;
    }

    const newSteps = [...steps];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    [newSteps[index], newSteps[targetIndex]] = [
      newSteps[targetIndex],
      newSteps[index],
    ];
    // Reorder
    newSteps.forEach((s, i) => {
      s.order = i + 1;
    });
    onChange(newSteps);
  };

  return (
    <div className="space-y-4">
      {steps.map((step, index) => (
        <div
          key={step.step_id}
          className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700"
        >
          <div className="flex items-start gap-3">
            {/* Drag handle / order */}
            <div className="flex flex-col items-center gap-1 pt-2">
              <button
                type="button"
                onClick={() => moveStep(index, "up")}
                disabled={index === 0}
                className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
              >
                <FontAwesomeIcon icon={faGripVertical} className="text-xs" />
              </button>
              <span className="text-sm font-medium text-slate-500">
                {step.order}
              </span>
              <button
                type="button"
                onClick={() => moveStep(index, "down")}
                disabled={index === steps.length - 1}
                className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
              >
                <FontAwesomeIcon icon={faGripVertical} className="text-xs" />
              </button>
            </div>

            {/* Step content */}
            <div className="flex-1 space-y-3">
              <input
                type="text"
                value={step.title}
                onChange={(e) => updateStep(index, { title: e.target.value })}
                placeholder="Step title..."
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="file"
                  ref={(el) => {
                    fileInputRefs.current[step.step_id] = el;
                  }}
                  className="hidden"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    void handleImageFile(index, f);
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRefs.current[step.step_id]?.click()}
                  disabled={uploadingForStepId === step.step_id}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/80 disabled:opacity-50"
                >
                  {uploadingForStepId === step.step_id ? (
                    <FontAwesomeIcon
                      icon={faCircleNotch}
                      className="animate-spin"
                    />
                  ) : (
                    <FontAwesomeIcon icon={faImage} />
                  )}
                  Add screenshot
                </button>
                <button
                  type="button"
                  onClick={() => wrapInstructionMarkdown(index, "bold")}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/80"
                  title="Bold — wraps selection, or inserts empty **…** with cursor inside"
                  aria-label="Insert bold"
                >
                  <FontAwesomeIcon icon={faBold} />
                  Bold
                </button>
                <button
                  type="button"
                  onClick={() => wrapInstructionMarkdown(index, "italic")}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/80"
                  title="Italic — wraps selection, or inserts empty emphasis with cursor ready to type"
                  aria-label="Insert italic"
                >
                  <FontAwesomeIcon icon={faItalic} />
                  Italic
                </button>
                <button
                  type="button"
                  onClick={() => setLinkDialogStepIndex(index)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/80"
                >
                  <FontAwesomeIcon icon={faLink} />
                  Add link
                </button>
                <button
                  type="button"
                  onClick={() => insertBulletMarkdownLine(index)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/80"
                  title="Insert a Markdown bullet (- ) at the cursor"
                >
                  <FontAwesomeIcon icon={faListUl} />
                  Bullet line
                </button>
              </div>
              <textarea
                ref={(el) => {
                  textareaRefs.current[step.step_id] = el;
                }}
                value={step.instructions}
                onChange={(e) =>
                  updateStep(index, { instructions: e.target.value })
                }
                placeholder="Step instructions (Markdown): **bold**, *italic*, - bullets, or use the toolbar…"
                rows={6}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[120px] font-mono text-sm"
              />
              <StepInstructionPreview
                key={step.step_id}
                source={step.instructions}
              />
            </div>

            {/* Remove button */}
            <button
              type="button"
              onClick={() => removeStep(index)}
              className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addStep}
        className="w-full py-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-slate-500 dark:text-slate-400 hover:border-indigo-400 hover:text-indigo-500 dark:hover:border-indigo-500 dark:hover:text-indigo-400 transition-colors flex items-center justify-center gap-2"
      >
        <FontAwesomeIcon icon={faPlus} />
        Add Step
      </button>

      <InstructionLinkDialog
        isOpen={linkDialogStepIndex !== null}
        onClose={closeLinkDialog}
        onInsert={handleInstructionLinkInsert}
      />
    </div>
  );
}

interface ChecklistEditorProps {
  checklist?: Checklist | null;
  onSave: (data: {
    name: string;
    description: string;
    bullet_points: string[];
    schedule_days_of_week: Weekday[];
    schedule_days_of_month: number[];
    ttl_days: number;
    steps: ChecklistStep[];
    default_assignee?: string;
    deadline?: string | null;
    timmy_review_default: TimmyReviewDefault;
  }) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

function ChecklistEditor({
  checklist,
  onSave,
  onCancel,
  isSaving,
}: ChecklistEditorProps) {
  const { addNotification } = useNotification();
  const [name, setName] = useState(checklist?.name || "");
  const [description, setDescription] = useState(checklist?.description || "");
  const [bulletPoints, setBulletPoints] = useState<string[]>(() =>
    checklist?.bullet_points?.length ? [...checklist.bullet_points] : [],
  );
  const [scheduleDaysOfWeek, setScheduleDaysOfWeek] = useState<Weekday[]>(
    checklist?.schedule_days_of_week || []
  );
  const [scheduleDaysOfMonth, setScheduleDaysOfMonth] = useState<number[]>(
    checklist?.schedule_days_of_month || []
  );
  const [ttlDays, setTtlDays] = useState(checklist?.ttl_days || 1);
  const [defaultAssignee, setDefaultAssignee] = useState(checklist?.default_assignee || "");
  const [deadline, setDeadline] = useState(checklist?.deadline || "");
  const [timmyReviewDefault, setTimmyReviewDefault] = useState<TimmyReviewDefault>(() =>
    normalizeTimmyReviewDefault(checklist?.timmy_review_default),
  );
  const [steps, setSteps] = useState<ChecklistStep[]>(() => checklist?.steps ?? []);

  useEffect(() => {
    if (!checklist) return;
    setName(checklist.name || "");
    setDescription(checklist.description || "");
    setBulletPoints(
      checklist.bullet_points?.length ? [...checklist.bullet_points] : [],
    );
    setScheduleDaysOfWeek(checklist.schedule_days_of_week || []);
    setScheduleDaysOfMonth(checklist.schedule_days_of_month || []);
    setTtlDays(checklist.ttl_days ?? 1);
    setDefaultAssignee(checklist.default_assignee || "");
    setDeadline(checklist.deadline || "");
    setTimmyReviewDefault(normalizeTimmyReviewDefault(checklist.timmy_review_default));
    setSteps(checklist.steps || []);
  }, [checklist?.checklist_id, checklist?.updated_at, checklist?.timmy_review_default]);

  const toggleWeekday = (day: Weekday) => {
    setScheduleDaysOfWeek(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      addNotification({
        type: "error",
        message: "Checklist name is required.",
      });
      return;
    }
    if (steps.length === 0) {
      addNotification({
        type: "error",
        message: "Add at least one step before saving.",
      });
      return;
    }
    const emptyTitleIdx = steps.findIndex((s) => !s.title.trim());
    if (emptyTitleIdx !== -1) {
      addNotification({
        type: "error",
        message: `Step ${emptyTitleIdx + 1} needs a title before you can save.`,
      });
      return;
    }

    const deadlineNorm = normalizeDeadlineHhMm(deadline);
    if (!isValidDeadlineHhMm(deadlineNorm)) {
      addNotification({
        type: "error",
        message: "Deadline must be 24-hour HH:MM (e.g. 17:30), or leave blank for no deadline.",
      });
      return;
    }

    await onSave({
      name: name.trim(),
      description: description.trim(),
      bullet_points: normalizedBulletPoints(bulletPoints),
      schedule_days_of_week: scheduleDaysOfWeek,
      schedule_days_of_month: scheduleDaysOfMonth,
      ttl_days: ttlDays,
      default_assignee: defaultAssignee || undefined,
      deadline: deadlineNorm ? deadlineNorm : null,
      timmy_review_default: timmyReviewDefault ?? "required",
      steps: stepsForChecklistApi(steps),
    });
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header with back button */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <FontAwesomeIcon
              icon={faArrowLeft}
              className="text-slate-500 dark:text-slate-400"
            />
          </button>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {checklist ? "Edit Checklist" : "New Checklist"}
          </h2>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !name.trim() || steps.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving && (
              <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
            )}
            <FontAwesomeIcon icon={faSave} />
            Save
          </button>
        </div>
      </div>

      {/* Scrollable form content */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="space-y-6">
            {/* Basic info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Checklist name..."
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Default Assignee
                </label>
                <select
                  value={defaultAssignee}
                  onChange={(e) => setDefaultAssignee(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">No default</option>
                  {OPS_TEAM_MEMBERS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Timmy review (default)
              </label>
              <select
                value={timmyReviewDefault}
                onChange={(e) =>
                  setTimmyReviewDefault(e.target.value as TimmyReviewDefault)
                }
                className="w-full max-w-xl px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="required">
                  Require Timmy review before completing each step
                </option>
                <option value="skip">
                  Timmy review off by default (runners can turn it on per step)
                </option>
              </select>
              <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                Applies when starting each step. The in-run &quot;Timmy review&quot; toggle still
                overrides this for that step.
              </p>
            </div>

            {/* Schedule */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Schedule
              </label>
              
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                  Days of week (empty = every day)
                </div>
                <div className="flex gap-1">
                  {WEEKDAYS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleWeekday(value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                        scheduleDaysOfWeek.includes(value)
                          ? "bg-indigo-600 text-white"
                          : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                    Days of month (comma-separated)
                  </div>
                  <input
                    type="text"
                    value={scheduleDaysOfMonth.join(", ")}
                    onChange={(e) => {
                      const days = e.target.value
                        .split(",")
                        .map(s => parseInt(s.trim()))
                        .filter(n => !isNaN(n) && n >= 1 && n <= 31);
                      setScheduleDaysOfMonth(days);
                    }}
                    placeholder="e.g. 1, 15"
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                    Deadline (HH:MM)
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                    onBlur={() =>
                      setDeadline((prev) => normalizeDeadlineHhMm(prev))
                    }
                    placeholder="e.g. 17:00 (optional)"
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono"
                  />
                </div>

                <div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                    Visible for (days)
                  </div>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={ttlDays}
                    onChange={(e) => setTtlDays(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Bullet points
              </label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                Short reminders shown on the dashboard and at the top of a run (optional).
              </p>
              <div className="space-y-2">
                {bulletPoints.map((line, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={line}
                      onChange={(e) => {
                        const next = [...bulletPoints];
                        next[i] = e.target.value;
                        setBulletPoints(next);
                      }}
                      placeholder={`Point ${i + 1}…`}
                      className="flex-1 min-w-0 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setBulletPoints(bulletPoints.filter((_, j) => j !== i))
                      }
                      className="p-2 text-slate-400 hover:text-red-500 rounded-lg"
                      aria-label={`Remove bullet ${i + 1}`}
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-sm" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setBulletPoints([...bulletPoints, ""])}
                className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-dashed border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-indigo-400 hover:text-indigo-600 dark:hover:border-indigo-500 dark:hover:text-indigo-400"
              >
                <FontAwesomeIcon icon={faPlus} />
                Add bullet
              </button>
              {bulletPoints.some((s) => s.trim()) && (
                <ChecklistBulletPoints
                  items={bulletPoints}
                  compact
                  className="mt-3"
                  heading="Preview"
                />
              )}
            </div>

            {/* Steps */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                Steps <span className="text-red-500">*</span>
              </label>
              <StepEditor steps={steps} onChange={setSteps} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ChecklistConfig() {
  const { addNotification } = useNotification();

  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingEditId, setLoadingEditId] = useState<string | null>(null);
  const [editingChecklist, setEditingChecklist] = useState<Checklist | null>(
    null
  );
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const data = await getChecklists(true);
      setChecklists(data);
    } catch (error) {
      console.error("Failed to load checklists:", error);
      addNotification({
        type: "error",
        message: "Failed to load checklists",
      });
    } finally {
      setIsLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openChecklistForEdit = useCallback(
    async (cl: Checklist) => {
      setIsCreating(false);
      setLoadingEditId(cl.checklist_id);
      try {
        const fresh = await getChecklist(cl.checklist_id);
        setEditingChecklist(fresh);
      } catch (error) {
        console.error("Failed to load checklist for edit:", error);
        addNotification({
          type: "error",
          message: "Could not load the latest checklist. Opening saved list copy.",
        });
        setEditingChecklist(cl);
      } finally {
        setLoadingEditId(null);
      }
    },
    [addNotification]
  );

  const handleSave = async (data: {
    name: string;
    description: string;
    bullet_points: string[];
    schedule_days_of_week: Weekday[];
    schedule_days_of_month: number[];
    ttl_days: number;
    steps: ChecklistStep[];
    default_assignee?: string;
    deadline?: string | null;
    timmy_review_default: TimmyReviewDefault;
  }) => {
    setIsSaving(true);
    try {
      if (editingChecklist) {
        const saved = await updateChecklist(editingChecklist.checklist_id, data);
        setChecklists((prev) => {
          const i = prev.findIndex((c) => c.checklist_id === saved.checklist_id);
          if (i === -1) return [...prev, saved];
          const next = [...prev];
          next[i] = saved;
          return next;
        });
        addNotification({
          type: "success",
          message: "Checklist updated",
        });
      } else {
        const created = await createChecklist(data);
        setChecklists((prev) => [...prev, created]);
        addNotification({
          type: "success",
          message: "Checklist created",
        });
      }
      setEditingChecklist(null);
      setIsCreating(false);
      await loadData();
    } catch (error) {
      console.error("Failed to save checklist:", error);
      addNotification({
        type: "error",
        message: formatChecklistSaveError(error),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (checklist: Checklist) => {
    try {
      await updateChecklist(checklist.checklist_id, {
        is_active: !checklist.is_active,
      });
      await loadData();
      addNotification({
        type: "success",
        message: checklist.is_active ? "Checklist disabled" : "Checklist enabled",
      });
    } catch (error) {
      console.error("Failed to toggle checklist:", error);
      addNotification({
        type: "error",
        message: "Failed to update checklist",
      });
    }
  };

  const handleExport = async () => {
    if (checklists.length === 0) {
      return;
    }

    setIsExporting(true);
    try {
      await downloadChecklistsXlsx(checklists);
      addNotification({
        type: "success",
        message: `Exported ${checklists.length} checklist${checklists.length === 1 ? "" : "s"} to Excel`,
      });
    } catch (error) {
      console.error("Failed to export checklists:", error);
      addNotification({
        type: "error",
        message: "Failed to export checklists to Excel",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDelete = async (checklist: Checklist) => {
    if (
      !confirm(
        `Are you sure you want to delete "${checklist.name}"? This will hide it from the dashboard.`
      )
    ) {
      return;
    }

    try {
      await deleteChecklist(checklist.checklist_id);
      await loadData();
      addNotification({
        type: "success",
        message: "Checklist deleted",
      });
    } catch (error) {
      console.error("Failed to delete checklist:", error);
      addNotification({
        type: "error",
        message: "Failed to delete checklist",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon
          icon={faCircleNotch}
          className="text-2xl text-indigo-500 animate-spin"
        />
      </div>
    );
  }

  // Show editor if creating or editing
  if (isCreating || editingChecklist) {
    return (
      <div className="flex flex-col h-full min-h-0 p-6">
        <ChecklistEditor
          key={isCreating ? "__create__" : editingChecklist!.checklist_id}
          checklist={editingChecklist}
          onSave={handleSave}
          onCancel={() => {
            setIsCreating(false);
            setEditingChecklist(null);
          }}
          isSaving={isSaving}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-shrink-0 gap-4">
        <OpsSubpageHeadingClassic
          title="Configure Checklists"
          titleClassName="text-lg font-bold text-slate-900 dark:text-white"
          backTo="/ops/checklists"
          hubWindowTitle="Ops Checklists"
          useOsLink
          linkClassName="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          subtitle={
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {checklists.length} checklist
              {checklists.length !== 1 ? "s" : ""}
            </p>
          }
        />

        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="checklist-config-export-excel"
            onClick={() => void handleExport()}
            disabled={checklists.length === 0 || isExporting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <FontAwesomeIcon
              icon={isExporting ? faCircleNotch : faFileExcel}
              className={isExporting ? "animate-spin" : undefined}
            />
            Export Excel
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingChecklist(null);
              setIsCreating(true);
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} />
            New Checklist
          </button>
        </div>
      </div>

      {/* Checklists list */}
      <div className="flex-1 min-h-0 overflow-auto space-y-3">
        {checklists.length === 0 ? (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            No checklists configured yet.{" "}
            <button
              type="button"
              data-testid="checklist-config-empty-create"
              onClick={() => {
                setEditingChecklist(null);
                setIsCreating(true);
              }}
              className="text-indigo-600 dark:text-indigo-400 hover:underline text-sm font-medium"
            >
              Create your first one!
            </button>
          </div>
        ) : (
          checklists.map((cl) => (
            <div
              key={cl.checklist_id}
              className={`bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 ${
                !cl.is_active ? "opacity-60" : ""
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-slate-900 dark:text-white">
                      {cl.name}
                    </h3>
                    <Badge color={cl.is_active ? "green" : "slate"}>
                      {cl.is_active ? "Active" : "Disabled"}
                    </Badge>
                    {cl.schedule_days_of_week.length > 0 && (
                      <Badge color="blue">
                        {cl.schedule_days_of_week.map(d => d.slice(0, 3)).join(", ")}
                      </Badge>
                    )}
                    {cl.schedule_days_of_month.length > 0 && (
                      <Badge color="purple">
                        Day {cl.schedule_days_of_month.join(", ")}
                      </Badge>
                    )}
                    {cl.schedule_days_of_week.length === 0 && cl.schedule_days_of_month.length === 0 && (
                      <Badge color="slate">Daily</Badge>
                    )}
                  </div>
                  {cl.description && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                      {cl.description}
                    </p>
                  )}
                  {cl.bullet_points && cl.bullet_points.length > 0 && (
                    <ChecklistBulletPoints
                      items={cl.bullet_points}
                      compact
                      className="mt-2"
                      heading="Key points"
                    />
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    {cl.steps.length} step{cl.steps.length !== 1 ? "s" : ""}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleToggleActive(cl)}
                    className={`p-2 rounded-lg transition-colors ${
                      cl.is_active
                        ? "text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                        : "text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                    }`}
                    title={cl.is_active ? "Disable" : "Enable"}
                  >
                    <FontAwesomeIcon
                      icon={cl.is_active ? faToggleOn : faToggleOff}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => void openChecklistForEdit(cl)}
                    disabled={loadingEditId === cl.checklist_id}
                    className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 dark:hover:text-indigo-400 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loadingEditId === cl.checklist_id ? (
                      <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />
                    ) : (
                      <FontAwesomeIcon icon={faEdit} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(cl)}
                    className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
